/** Formatting helpers for the crypto payments manage page. Pure, locale-aware through `locale`. */

/** `0x12345678…abcdef` for hashes and ids that would otherwise set the table width. */
export function truncateMiddle(value: string, head = 8, tail = 6): string {
  if (value.length <= head + tail + 1) return value;
  return `${value.slice(0, head)}…${value.slice(value.length - tail)}`;
}

/**
 * A base-unit integer string to a decimal string (`"1500000", 6` → `"1.5"`).
 * Falls back to the raw value when the input is not an integer or the
 * decimals are unknown, so a surprising payload still renders something.
 */
export function formatBaseAmount(base: string | null, decimals: number | null): string | null {
  if (base === null) return null;
  if (decimals === null || decimals < 0 || !/^-?\d+$/.test(base)) return base;
  const isNegative = base.startsWith('-');
  const digits = isNegative ? base.slice(1) : base;
  const padded = digits.padStart(decimals + 1, '0');
  const whole = padded.slice(0, padded.length - decimals);
  const fraction = padded.slice(padded.length - decimals).replace(/0+$/, '');
  return `${isNegative ? '-' : ''}${whole}${fraction ? `.${fraction}` : ''}`;
}

/** `"12.5", "USD"` → `"$12.50"` in the given locale; null when either part is missing or not a number. */
export function formatFiat(amount: string | null, currency: string | null, locale?: string): string | null {
  if (amount === null || currency === null) return null;
  const value = Number(amount);
  if (!Number.isFinite(value)) return null;
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value);
  } catch {
    // An unknown ISO code must not take the row down with it.
    return `${value.toFixed(2)} ${currency}`;
  }
}

/** `2.5` → `"2.5%"` (`"2,5 %"` in French): a percent held as a percent, not a ratio. */
export function formatPercent(value: number, locale?: string): string {
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 2 }).format(value / 100);
}

/** Basis points as stored by crypto-api (`250`) to a display percent (`"2.5%"`). */
export function formatBps(bps: number, locale?: string): string {
  return formatPercent(bps / 100, locale);
}

const PERCENT_INPUT_REGEX = /^\d{1,3}(?:[.,]\d{1,2})?$/;

/** crypto-api caps a share at 9999 bps: the merchant always keeps part of a payment. */
export const MAX_SHARE_PERCENT = 99.99;

/**
 * A typed percent to a number: 0–99.99, at most two decimals, comma or dot.
 * Null when the text is not such a percent, so the caller can refuse the submit
 * instead of silently rounding what the operator wrote.
 */
export function parsePercentInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (!PERCENT_INPUT_REGEX.test(trimmed)) return null;
  const value = Number(trimmed.replace(',', '.'));
  if (!Number.isFinite(value) || value < 0 || value > MAX_SHARE_PERCENT) return null;
  return Math.round(value * 100) / 100;
}

/** Only http(s) links from the API are rendered as links (an explorer URL is data, not trusted markup). */
export function safeHttpUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.toString() : null;
  } catch {
    return null;
  }
}
